/**
 * SocialSignInButtons Component
 *
 * Renders Google and Outlook sign-in buttons with proper SVG icons.
 * Handles social authentication integration points.
 */

import { Loading01, Mail01 } from '@untitled-ui/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AUTH_SECONDARY_BUTTON_CLASS,
  AUTH_TILE_BUTTON_CLASS,
} from '@/components/auth/auth-button-styles';
import { authOptionClass } from '@/components/brand/entry-styles';
import { useSocialSignIn } from '@/hooks/use-social-sign-in';
import { isDesktopPlatform } from '@/lib/desktop/bridge';

interface SocialSignInButtonsProps {
  /** Web only: opens the secure-link route. */
  readonly onMagicLinkClick?: () => void;
  /** Desktop only: opens the in-shell sign-in-code panel. */
  readonly onEmailCodeClick?: () => void;
  readonly className?: string;
  readonly variant?: 'stack' | 'tiles';
  readonly isDisabled?: boolean;
  readonly onLoadingChange?: (isLoading: boolean) => void;
}

const CONTINUE_IN_BROWSER_KEY = 'socialSignIn.continueInBrowser';

function socialPresentation(desktop: boolean, tiles: boolean, loadingProvider: string | null) {
  const labels = tiles
    ? {
        google: 'socialSignIn.googleName',
        microsoft: 'socialSignIn.microsoftName',
        email: 'socialSignIn.secureLinkName',
      }
    : {
        google: 'socialSignIn.google',
        microsoft: 'socialSignIn.microsoft',
        email: 'socialSignIn.secureLink',
      };
  const webClass = tiles ? AUTH_TILE_BUTTON_CLASS : AUTH_SECONDARY_BUTTON_CLASS;
  return {
    methodClass: desktop ? authOptionClass : webClass,
    labelClass: desktop || tiles ? '' : 'text-sm font-medium text-[var(--color-fg-primary)]',
    googleLabel: loadingProvider === 'google' ? CONTINUE_IN_BROWSER_KEY : labels.google,
    microsoftLabel: loadingProvider === 'azure' ? CONTINUE_IN_BROWSER_KEY : labels.microsoft,
    emailLabel: desktop ? 'socialSignIn.emailCode' : labels.email,
  };
}

const SocialSignInButtons = ({
  onMagicLinkClick,
  onEmailCodeClick,
  className = '',
  variant = 'stack',
  isDisabled = false,
  onLoadingChange,
}: SocialSignInButtonsProps) => {
  const { t } = useTranslation('onboarding');
  const { signIn, loadingProvider, cancelSignIn, handoffAbandoned } = useSocialSignIn();
  const desktop = isDesktopPlatform();
  const controlsDisabled = isDisabled || loadingProvider !== null;
  // The desktop shell sits on the entry canvas, not on the web page's white
  // card over cool grey. Its pills come from the entry tokens for the reason
  // `entry-styles.ts` records: the web chrome carries theme-flipping semantic
  // colours, and this canvas is the same cream in both themes.
  //
  // ONE class for both methods, on both surfaces. `entry-styles.ts` allows the
  // stack exactly one filled pill, and on desktop that is now the brand-purple
  // submit - a filled social pill above it would put two competing primaries on
  // one screen. Web has always drawn both as secondaries, so a single shared
  // class is also what makes the two surfaces read as one product. Ranking
  // Google above Microsoft is a product call; neither surface asserts it here.
  const tiles = variant === 'tiles' && !desktop;
  const { methodClass, labelClass, googleLabel, microsoftLabel, emailLabel } = socialPresentation(
    desktop,
    tiles,
    loadingProvider,
  );

  useEffect(() => {
    onLoadingChange?.(loadingProvider !== null);
  }, [loadingProvider, onLoadingChange]);

  const startSignIn = (provider: 'google' | 'azure') => {
    if (controlsDisabled) {
      return;
    }
    onLoadingChange?.(true);
    signIn(provider).catch(() => {
      onLoadingChange?.(false);
    });
  };

  return (
    <div className={`${tiles ? 'contents' : 'space-y-2.5'} ${className}`}>
      {/* Google Sign In Button */}
      <button
        aria-busy={loadingProvider === 'google'}
        aria-label={t('socialSignIn.google')}
        className={methodClass}
        disabled={controlsDisabled}
        type="button"
        onClick={() => startSignIn('google')}
      >
        {loadingProvider === 'google' ? (
          <Loading01 aria-hidden="true" className="size-5 shrink-0 animate-spin" />
        ) : (
          <svg
            aria-hidden="true"
            className="size-5 shrink-0"
            fill="none"
            viewBox="0 0 25 25"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M11.3135 2.07257C12.401 1.95107 13.0445 1.95107 14.213 2.07257C16.2814 2.37871 18.1988 3.3348 19.688 4.80257C18.6817 5.75377 17.6886 6.71887 16.709 7.69757C14.833 6.10757 12.739 5.74057 10.427 6.59657C8.731 7.37657 7.55 8.64057 6.884 10.3886C5.79566 9.57832 4.7215 8.74919 3.662 7.90157C3.58837 7.86281 3.50427 7.84862 3.422 7.86107C5.105 4.61607 7.735 2.68607 11.312 2.07107"
              fill="#F44336"
              fillRule="evenodd"
              opacity="0.987"
            />
            <path
              clipRule="evenodd"
              d="M3.41901 7.86123C3.50401 7.84823 3.58451 7.86173 3.66051 7.90173C4.72001 8.74935 5.79417 9.57847 6.88251 10.3887C6.71125 11.0698 6.60329 11.7653 6.56001 12.4662C6.59701 13.1442 6.70451 13.8097 6.88251 14.4627L3.50001 17.1552C2.02701 14.0772 2.00001 10.9792 3.41901 7.86123Z"
              fill="#FFC107"
              fillRule="evenodd"
              opacity="0.997"
            />
            <path
              clipRule="evenodd"
              d="M19.5275 20.4161C18.4743 19.4872 17.3717 18.616 16.2245 17.8061C17.3745 16.9941 18.0725 15.8801 18.3185 14.4641H12.683V10.5506C15.933 10.5236 19.1815 10.5511 22.4285 10.6331C23.0445 13.9781 22.333 16.9941 20.294 19.6811C20.0516 19.9388 19.7947 20.1841 19.5275 20.4161Z"
              fill="#448AFF"
              fillRule="evenodd"
              opacity="0.999"
            />
            <path
              clipRule="evenodd"
              d="M6.8825 14.4639C8.1125 17.5209 10.3675 18.9479 13.6475 18.7449C14.5682 18.6383 15.451 18.3166 16.2245 17.8059C17.3725 18.6179 18.4735 19.4879 19.5275 20.4159C17.8575 21.9165 15.7282 22.807 13.487 22.9419C12.9778 22.9826 12.4662 22.9826 11.957 22.9419C8.139 22.4919 5.32 20.5629 3.5 17.1549L6.8825 14.4639Z"
              fill="#43A047"
              fillRule="evenodd"
              opacity="0.993"
            />
          </svg>
        )}
        <span className={labelClass}>{t(googleLabel)}</span>
      </button>

      {/* Microsoft Sign In Button */}
      <button
        aria-busy={loadingProvider === 'azure'}
        aria-label={t('socialSignIn.microsoft')}
        className={methodClass}
        disabled={controlsDisabled}
        type="button"
        onClick={() => startSignIn('azure')}
      >
        {loadingProvider === 'azure' ? (
          <Loading01 aria-hidden="true" className="size-5 shrink-0 animate-spin" />
        ) : (
          <svg
            aria-hidden="true"
            className="size-5 shrink-0"
            fill="none"
            viewBox="0 0 21 21"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect fill="#F25022" height="9.5" width="9.5" x="0.5" y="0.5" />
            <rect fill="#7FBA00" height="9.5" width="9.5" x="11" y="0.5" />
            <rect fill="#00A4EF" height="9.5" width="9.5" x="0.5" y="11" />
            <rect fill="#FFB900" height="9.5" width="9.5" x="11" y="11" />
          </svg>
        )}
        <span className={labelClass}>{t(microsoftLabel)}</span>
      </button>

      {/* The same slot, and deliberately NOT the same method. A clickable link
          is the one thing the shell cannot use: office staff read email on a
          phone, so the link opens there rather than on the desktop that is
          waiting, and mail scanners prefetch URLs and redeem the credential
          before the human clicks. A typed code survives both. */}
      <button
        aria-label={t(desktop ? 'socialSignIn.emailCode' : 'socialSignIn.secureLink')}
        className={methodClass}
        disabled={controlsDisabled}
        type="button"
        onClick={desktop ? onEmailCodeClick : onMagicLinkClick}
      >
        <Mail01
          aria-hidden="true"
          className={`size-5 shrink-0 ${desktop ? '' : 'text-[var(--color-fg-tertiary)]'}`}
        />
        <span className={labelClass}>{t(emailLabel)}</span>
      </button>

      {loadingProvider ? (
        <>
          {/* The only control left enabled while a handoff is pending, and the
              reason this component has a way out at all: cancelling in the
              browser sends nothing back, so the shell cannot detect it. Without
              this the user's only recovery is restarting the app. */}
          <button
            className="col-span-full w-full py-1 text-center text-sm font-medium text-entry-ink/60 underline underline-offset-2 transition-colors hover:text-entry-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-entry-ink focus-visible:outline-solid"
            type="button"
            onClick={cancelSignIn}
          >
            {t('socialSignIn.cancelHandoff')}
          </button>
          <output aria-live="polite" className="sr-only">
            {t(CONTINUE_IN_BROWSER_KEY)}
          </output>
        </>
      ) : null}

      {handoffAbandoned ? (
        // `<output>` rather than an alert: an abandoned sign-in is a state the
        // user usually caused, not an error to interrupt them with. It is
        // announced at all because the visible change is otherwise just controls
        // quietly becoming clickable again.
        <output className="col-span-full block py-1 text-center text-sm text-entry-ink/70">
          {t('socialSignIn.handoffNotCompleted')}
        </output>
      ) : null}
    </div>
  );
};

export default SocialSignInButtons;
